import React from 'react';
import PropTypes from 'prop-types';
import {
  Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import IconButton from '@material-ui/core/IconButton';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import Close from '@material-ui/icons/Close';
import { businessOptionsErrorMessage } from '../../../services/DriverDirectory';
import {
  coverageName, coverageErrorMessage, coverageRatioLabel, COVERAGE_COLOR,
  RATIO_LABELS, MAPPING_DIAGNOSTICS, DIAGNOSTIC_LABELS,
} from '../../../services/TerritoryCoverage';
import { useStyles } from './Coverage.styles';

// Optional debug metadata stays nested and never supplies the primary metrics.
function MetadataValues({ values }) {
  const classes = useStyles();
  return (
    <dl className={classes.definitions}>
      {Object.entries(values).map(([key, value]) => (
        <React.Fragment key={key}>
          <dt>{key.replace(/_/g, ' ')}</dt>
          <dd>{value !== null && typeof value === 'object'
            ? <MetadataValues values={value} />
            : value === null ? 'None' : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
MetadataValues.propTypes = { values: PropTypes.object.isRequired };

export function CoverageDetails({ feature }) {
  const classes = useStyles();
  const p = feature.properties;
  return (
    <section className={classes.detail} aria-label="Selected territory">
      <Typography variant="subtitle2">{coverageName(feature)} · ID {p.id}</Typography>
      <Typography variant="body2">{coverageRatioLabel(feature)}</Typography>
      {!feature.geometry && <p className={classes.warning}>Boundary unavailable. Locations cannot be mapped into this territory.</p>}
      {feature.geometry && feature.displayArea?.status === 'unavailable' && (
        <p className={classes.warning}>Approximate area unavailable. Server coverage metrics are unchanged.</p>
      )}
      <dl className={classes.metrics}>
        <div className={classes.metric}><dt>Orders</dt><dd>{p.dispatchable_order_count}</dd></div>
        <div className={classes.metric}><dt>Mapped drivers</dt><dd>{p.active_driver_count}</dd></div>
      </dl>
      {p.v1_comparison && (
        <details className={classes.section}>
          <summary className={classes.summary}>V1 territory comparison</summary>
          <MetadataValues values={p.v1_comparison} />
        </details>
      )}
    </section>
  );
}
CoverageDetails.propTypes = { feature: PropTypes.object.isRequired };

export function CoverageMetadata({ data, now, stale }) {
  const classes = useStyles();
  const date = new Date(data.as_of_utc);
  const age = Math.max(0, Math.floor((Number(now) - date.getTime()) / 1000));
  return (
    <>
      <div className={classes.section}>
        <Typography variant="body2">
          As of <time dateTime={data.as_of_utc}>{date.toLocaleString()}</time> (local)
        </Typography>
        <div className={classes.muted}>Snapshot age: {Number.isFinite(age) ? `${age}s` : 'unknown'}</div>
        {stale && <div role="status" className={classes.stale}>STALE SNAPSHOT — not a current view. Refresh before relying on these figures.</div>}
      </div>
      <section className={classes.section} aria-label="Business totals">
        <Typography variant="subtitle2">Business totals</Typography>
        <dl className={classes.metrics}>
          <div className={classes.metric}><dt>Clocked-in drivers</dt><dd>{data.business_summary.active_driver_count}</dd></div>
          <div className={classes.metric}><dt>Current orders</dt><dd>{data.business_summary.current_order_count}</dd></div>
        </dl>
      </section>
      <details className={classes.section}>
        <summary className={classes.summary}>Snapshot details</summary>
        <p className={classes.muted}>
          Business totals include unmapped drivers and orders. Current orders also include
          unfinished stages that are not dispatchable. Territory Orders count mapped
          dispatchable orders only; territory sums need not equal business totals.
          The ratio is Orders divided by mapped drivers, without urgency weights or a target.
          Map areas are approximate display geometry; server counts are unchanged.
        </p>
        {data.mapping_diagnostics && (
          <section aria-label="Mapping diagnostics">
            <Typography variant="subtitle2">Mapping diagnostics</Typography>
            <MetadataValues values={Object.fromEntries(MAPPING_DIAGNOSTICS
              .filter((key) => Object.prototype.hasOwnProperty.call(data.mapping_diagnostics, key))
              .map((key) => [DIAGNOSTIC_LABELS[key] || key, data.mapping_diagnostics[key]]))} />
            <p className={classes.muted}>Diagnostic categories can overlap; do not sum them into a population count.</p>
             <p className={classes.muted}>Device GPS diagnostics do not exclude valid run pins. Run update time is not GPS capture time.</p>
          </section>
        )}
        {data.model && (
          <section aria-label="Ratio model">
            <Typography variant="subtitle2">Ratio model</Typography>
            <MetadataValues values={data.model} />
          </section>
        )}
        {data.v1_comparison && (
          <details className={classes.section}>
            <summary className={classes.summary}>V1 snapshot comparison</summary>
            <MetadataValues values={data.v1_comparison} />
          </details>
        )}
      </details>
    </>
  );
}
CoverageMetadata.propTypes = {
  data: PropTypes.object.isRequired,
  now: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(Date)]),
  stale: PropTypes.bool,
};

export function CoveragePanel({
  collapsed = false, onCollapsed, embedded = false,
  enabled, onEnabled, options, businessId, onBusiness, data, error, isFetching,
  refetch, now, stale, features, ratioState = 'all', onRatioState, selectedId, onSelect,
}) {
  const classes = useStyles();
  const selected = features.find((feature) => feature.properties.id === selectedId);
  const unauthorized = error?.status === 403 || error?.response?.status === 403;
  return (
    <aside className={`${classes.panel} ${collapsed ? classes.collapsed : ''}`} aria-label="Territory coverage">
      <div className={`${classes.heading} ${classes.panelHeading}`}>
        <Typography variant="subtitle1" component="h2">Territory coverage</Typography>
        <FormControlLabel
          className={classes.layerToggle}
          control={<Checkbox size="small" color="primary" checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />}
          label="Show layer"
        />
        <IconButton
          size="small" className={classes.sizeToggle}
          aria-label={embedded ? 'Close coverage' : collapsed ? 'Expand territory coverage' : 'Minimize territory coverage'}
          aria-expanded={!collapsed} aria-controls="territory-coverage-body"
          onClick={() => onCollapsed(!collapsed)}
        >
          {embedded ? <Close /> : collapsed ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </div>
      <div id="territory-coverage-body" hidden={collapsed}>
        {enabled && (
          <>
            <TextField
              id="coverage-business" select fullWidth size="small" variant="outlined" label="Authorized business"
              value={businessId} onChange={(e) => onBusiness(e.target.value)}
              disabled={options.status !== 'success' || !options.data.length}
            >
              <MenuItem value="">Choose a business</MenuItem>
              {(options.status === 'success' ? options.data : []).map(({ id, name }) => (
                <MenuItem key={id} value={String(id)}>{name || `Business ${id}`} (ID: {id})</MenuItem>
              ))}
            </TextField>
            {options.status === 'loading' && <div role="status" aria-label="Loading authorized businesses"><Skeleton height={35} /></div>}
            {options.status === 'error' && (
              <div role="alert" className={classes.section}>
                <Typography variant="body2">{businessOptionsErrorMessage(options.error)}</Typography>
                <Button size="small" onClick={options.reload}>Retry businesses</Button>
              </div>
            )}
            {options.status === 'success' && !options.data.length && (
              <p role="status">No authorized active businesses. Contact an administrator for access.</p>
            )}
            {!!businessId && (
              <>
                <div className={classes.heading}>
                  <span className={classes.muted}>Read-only snapshot</span>
                  <Button size="small" onClick={refetch} disabled={isFetching}>Refresh coverage</Button>
                </div>
                {isFetching && <div role="status" aria-label="Loading coverage"><Skeleton height={30} /><Skeleton height={30} /></div>}
                {error && (
                  <div role="alert" className={classes.stale}>
                    {unauthorized && 'Coverage access denied. No coverage is shown. '}
                    {coverageErrorMessage(error)}
                    {!isFetching && <Button size="small" onClick={refetch}>Retry coverage</Button>}
                  </div>
                )}
                {!data && !error && !isFetching && <p role="status">Coverage unavailable. Refresh to request a snapshot.</p>}
                {data && !unauthorized && (
                  <>
                    <CoverageMetadata data={data} now={now} stale={stale || !!error} />
                    <div className={classes.legend} aria-label="Coverage legend">
                      <span><i className={classes.swatch} style={{ background: COVERAGE_COLOR }} />Orders per mapped driver</span>
                    </div>
                    <TextField id="coverage-ratio-state" select fullWidth size="small" label="Ratio state" value={ratioState} onChange={(e) => onRatioState(e.target.value)}>
                      <MenuItem value="all">All territories</MenuItem>
                      {Object.entries(RATIO_LABELS).map(([key, text]) => <MenuItem key={key} value={key}>{text}</MenuItem>)}
                    </TextField>
                    {data.features.length === 0 && <p role="status">No territories for this business. This does not mean there are no orders.</p>}
                    {data.features.length > 0 && !features.length && <p role="status">No territories match this ratio state.</p>}
                    <ul className={classes.list} aria-label="Territory coverage list">
                      {features.map((feature) => {
                        const p = feature.properties;
                        return (
                          <li key={p.id}>
                            <button type="button" className={classes.row} aria-pressed={selectedId === p.id} onClick={() => onSelect(p.id)}>
                              <span>
                                <strong>{coverageName(feature)}</strong><br />
                                <small>ID {p.id}</small>
                                {!feature.geometry && <span className={classes.warning}><br />Boundary unavailable</span>}
                                {feature.geometry && feature.displayArea?.status === 'unavailable' && <span className={classes.warning}><br />Approximate area unavailable</span>}
                              </span>
                              <span className={classes.rowNumbers}>
                                <strong>{coverageRatioLabel(feature)}</strong><br />
                                <small>Orders: {p.dispatchable_order_count} · Mapped drivers: {p.active_driver_count}</small>
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    {selected && <CoverageDetails feature={selected} />}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
CoveragePanel.propTypes = {
  embedded: PropTypes.bool,
  collapsed: PropTypes.bool, onCollapsed: PropTypes.func.isRequired,
  enabled: PropTypes.bool.isRequired, onEnabled: PropTypes.func.isRequired,
  options: PropTypes.object.isRequired, businessId: PropTypes.string.isRequired,
  onBusiness: PropTypes.func.isRequired, data: PropTypes.object, error: PropTypes.object,
  isFetching: PropTypes.bool, refetch: PropTypes.func.isRequired,
  now: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(Date)]),
  stale: PropTypes.bool, features: PropTypes.array.isRequired,
  ratioState: PropTypes.string, onRatioState: PropTypes.func.isRequired,
  selectedId: PropTypes.number, onSelect: PropTypes.func.isRequired,
};
